'use client'

import { ArrowUp, Plus } from 'lucide-react'
import { nanoid } from 'nanoid'
import type { FormEvent, KeyboardEvent } from 'react'
import {
  buildPublicComposerDraft,
  writePublicComposerDraft,
} from '@/components/chat/public-composer-draft'
import { useOpenAuthModal } from '@/components/marketing/v2/use-auth-modal'
import { runPromptDemo } from './prompt-demo'
import {
  PROMPT_COMPOSER_EXAMPLE,
  PROMPT_COMPOSER_PLACEHOLDER,
  PROMPT_SCENES,
} from './prompt-showcase-content'

/** The form's ref callback: runs the typing demo, returns its teardown. */
function attachPromptDemo(form: HTMLFormElement) {
  return runPromptDemo(form, PROMPT_SCENES)
}

/**
 * Store the prompt as the signed-out composer draft — the same slot the
 * public launch composer writes — so the signed-in launcher on `/home`
 * restores it after sign-up. Storage can throw (private mode, quota); the
 * visitor still reaches sign-up, just without the prefill.
 */
function savePromptForSignUp(text: string) {
  try {
    writePublicComposerDraft(
      buildPublicComposerDraft({
        chatId: nanoid(),
        text,
        customBrandId: null,
        files: [],
      })
    )
  } catch {
    // Best effort: the prompt is a convenience, sign-up is the goal.
  }
}

/** Enter sends, Shift+Enter breaks the line, IME composition is left alone. */
function submitOnEnter(event: KeyboardEvent<HTMLTextAreaElement>) {
  if (
    event.key !== 'Enter' ||
    event.shiftKey ||
    event.nativeEvent.isComposing
  ) {
    return
  }
  event.preventDefault()
  event.currentTarget.form?.requestSubmit()
}

/**
 * The prompt box on the golden-hour sky: a borderless card lifted by a warm
 * shadow, with a soft white halo while it has focus. It opens with the
 * design's example prompt typed in, then (inside the showcase panel) types
 * and erases each scene's prompt until the visitor reaches for it; sending
 * hands whatever is in the box to sign-up. Before hydration the form is a plain GET to sign-up: the textarea
 * has no `name`, so the visitor's text never lands in the URL.
 */
export function PromptComposer({ signUpHref }: { signUpHref: string }) {
  const openAuth = useOpenAuthModal()

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const field = event.currentTarget.querySelector('textarea')
    const text = field instanceof HTMLTextAreaElement ? field.value.trim() : ''

    if (!text) {
      if (field instanceof HTMLTextAreaElement) {
        field.focus()
      }
      return
    }

    savePromptForSignUp(text)
    openAuth()
  }

  return (
    <form
      ref={attachPromptDemo}
      action={signUpHref}
      method="get"
      onSubmit={handleSubmit}
      className="relative flex h-[136px] w-full max-w-xl flex-col justify-between gap-2 rounded-2xl bg-mk-card px-4 pt-4 pb-3 shadow-[0_0_0_1px_rgb(0_0_0/0.04),0_16px_40px_-16px_rgb(96_40_0/0.45)] transition-shadow duration-150 focus-within:shadow-[0_0_0_4px_rgb(255_255_255/0.55),0_16px_40px_-16px_rgb(96_40_0/0.45)] md:h-[148px] md:px-5 md:pt-5 md:pb-4"
    >
      <textarea
        aria-label="Describe the email you want Brew to create"
        defaultValue={PROMPT_COMPOSER_EXAMPLE}
        placeholder={PROMPT_COMPOSER_PLACEHOLDER}
        onKeyDown={submitOnEnter}
        rows={3}
        className="min-h-0 w-full flex-1 resize-none bg-transparent p-0 font-mk-sans text-[16px] text-mk-ink leading-6 outline-none placeholder:text-mk-muted"
      />
      <div className="flex w-full items-center justify-between">
        <Plus
          aria-hidden="true"
          strokeWidth={1.8}
          className="size-4 shrink-0 text-mk-subtle"
        />
        <button
          type="submit"
          aria-label="Create with Brew"
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-mk-brand-border bg-mk-brand text-white shadow-mk-button transition-[filter,transform] duration-150 hover:brightness-105 focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2 active:scale-95"
        >
          <ArrowUp aria-hidden="true" strokeWidth={2.25} className="size-3.5" />
        </button>
      </div>
    </form>
  )
}
